import { TemplatePrompts } from '@kimuno/api-sdk';
import i18next from 'i18next';

import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import type { IconName } from '../../../utils/getMuiIcon';
import { ActiveView, CORRECTIONS_CARD_ID, INITIAL_POSITION } from './constants';
import type { TemplatePromptItem } from './TemplatePromptForm/AIAssistantTemplatePromptList';

interface CalculateDraggableBoundariesParams {
	windowWidth: number;
	windowHeight: number;
	elementWidth: number;
	elementHeight: number;
}

export const calculateDraggableBoundaries = ({
	windowWidth,
	windowHeight,
	elementWidth,
	elementHeight,
}: CalculateDraggableBoundariesParams) => ({
	left: -(windowWidth - elementWidth - INITIAL_POSITION.right),
	right: INITIAL_POSITION.right,
	top: -INITIAL_POSITION.top,
	bottom: windowHeight - elementHeight - INITIAL_POSITION.top,
});

// Onboarding "Neu" chip on the simplify-language card, shown through 2026-11-01 inclusive.
const SIMPLIFY_LANGUAGE_NEW_CHIP_END = new Date(2026, 10, 2);

export const isSimplifyLanguageNew = (now: Date = new Date()): boolean =>
	now < SIMPLIFY_LANGUAGE_NEW_CHIP_END;

export const assistantTemplatePrompts: Partial<
	Record<
		ActiveView,
		Array<{
			id: TemplatePrompts;
			title: string;
			subheader: string;
			icon: IconName;
			chip?: 'new' | 'beta';
		}>
	>
> = {
	[ActiveView.UNIT_ENTRY]: [
		{
			id: TemplatePrompts.CREATE_LEARNING_POSTER,
			title: i18next.t('Create learning poster'),
			subheader: i18next.t('Summarize core content visually'),
			icon: 'boldGallery',
			chip: 'new',
		},
		{
			id: TemplatePrompts.CREATE_SUMMARY,
			title: i18next.t('Write summary'),
			subheader: i18next.t('Summarise content briefly and clearly'),
			icon: 'textDocumentBold',
		},
		{
			id: TemplatePrompts.CREATE_SIMPLIFIED_LANGUAGE,
			title: i18next.t('Simplify language'),
			subheader: i18next.t('Create a simple or easy-language version'),
			icon: 'simplifyBold',
			// Getter, so a tab left open past the cutoff drops the chip on its next render.
			get chip() {
				return isSimplifyLanguageNew() ? ('new' as const) : undefined;
			},
		},
		{
			id: TemplatePrompts.CREATE_DIFFERENTIATED_TASKS,
			title: i18next.t('Differentiate tasks'),
			subheader: i18next.t('Create three levels'),
			icon: 'shapesBold',
		},
		{
			id: TemplatePrompts.CREATE_QUIZ,
			title: i18next.t('Create quiz'),
			subheader: i18next.t('Check understanding with multiple-choice questions'),
			icon: 'bulbBold',
		},
		{
			id: TemplatePrompts.CREATE_LESSON_PLAN,
			title: i18next.t('Create a lesson plan'),
			subheader: i18next.t('Planning for a 45-minute lesson'),
			icon: 'addDocumentBold',
		},
		{
			id: TemplatePrompts.CREATE_INTRODUCTORY_LESSON,
			title: i18next.t('Formulate initial impulse'),
			subheader: i18next.t('Create an introduction for a lesson'),
			icon: 'slidesBold',
		},
		{
			id: TemplatePrompts.CREATE_ADDITIONAL_TASKS,
			title: i18next.t('Insert extension tasks'),
			subheader: i18next.t('Provide in-depth questions or additional tasks'),
			icon: 'checklistBold',
		},
		{
			id: TemplatePrompts.CREATE_SUPPLEMENTAL_AIDS,
			title: i18next.t('Supplement aids'),
			subheader: i18next.t('Add explanations and tips'),
			icon: 'starWandBold',
		},
	],
	[ActiveView.FREE_DRAFT_ENTRY]: [
		{
			id: TemplatePrompts.CREATE_WORKSHEET_FREE_DRAFT,
			title: i18next.t('Create worksheet'),
			subheader: i18next.t('Differenzierte Aufgaben'),
			icon: 'createWorksheet',
		},
		{
			id: TemplatePrompts.CREATE_LESSON_PLAN_FREE_DRAFT,
			title: i18next.t('Lesson planning'),
			subheader: i18next.t('Lesson planning for 45 or 90 minutes'),
			icon: 'aiLessonPlanning',
		},
		{
			id: TemplatePrompts.CREATE_GROUP_WORK_FREE_DRAFT,
			title: i18next.t('Group work'),
			subheader: i18next.t('For effective team learning'),
			icon: 'aiGroupTasks',
		},
		{
			id: TemplatePrompts.CREATE_ESSAY_FREE_DRAFT,
			title: i18next.t('Essay'),
			subheader: i18next.t('Example essay for analysis'),
			icon: 'essayTemplate',
		},
		{
			id: TemplatePrompts.CREATE_TOPIC_EXPLANATION_FREE_DRAFT,
			title: i18next.t('Topic explanation'),
			subheader: i18next.t('Simple explanations'),
			icon: 'explainTopic',
		},
		{
			id: TemplatePrompts.CREATE_LEARNING_OBJECTIVE_ASSERTION_FREE_DRAFT,
			title: i18next.t('Learning objective assertion'),
			subheader: i18next.t('Query for knowledge verification'),
			icon: 'aiLearningObjectiveGeneration',
		},
		{
			id: TemplatePrompts.CREATE_LESSON_SERIES_FREE_DRAFT,
			title: i18next.t('Lesson series planning'),
			subheader: i18next.t('Planning multiple hours'),
			icon: 'aiLessonSeriesPlanning',
		},
	],
};

export interface MediaCreationTool {
	id: TemplatePrompts;
	title: string;
	subheader: string;
	icon: IconName;
	chip?: 'new' | 'beta';
}

export const mediaCreationTools: MediaCreationTool[] = [
	{
		id: TemplatePrompts.CREATE_IMAGE,
		title: i18next.t('Generate image'),
		subheader: i18next.t('Quickly create suitable classroom images'),
		icon: 'boldGallery',
	},
];

export const podcastTools: MediaCreationTool[] = [
	{
		id: TemplatePrompts.CREATE_PODCAST,
		title: i18next.t('Generate podcast'),
		subheader: i18next.t('Generate audio teaching materials'),
		icon: 'microphone',
		chip: 'beta',
	},
];

// `icon` is required by the type but stays unrendered while `chip` is set. The id
// itself lives in `./constants` alongside `TemplatePromptCardId`.
export const correctionsToolCard: TemplatePromptItem = {
	id: CORRECTIONS_CARD_ID,
	title: i18next.t('Correct handwritten texts'),
	subheader: i18next.t('Annotation & feedback'),
	icon: 'checklistBold',
	chip: 'new',
};

/**
 * Formats an array of numbers into a string representation of ranges
 * Example: [1,2,3,5,6,7,8,9,10] => "1-3, 5-10"
 * @param numbers - Array of numbers to format
 * @param separators - optional range/join separators; defaults preserve the
 *   existing `-` (within a range) and `, ` (between ranges) output.
 * @returns Formatted string of ranges
 */
export const formatNumberRanges = (
	numbers: number[],
	separators?: { rangeSeparator?: string; joinSeparator?: string }
): string => {
	if (!numbers.length) return '';

	const rangeSeparator = separators?.rangeSeparator ?? '-';
	const joinSeparator = separators?.joinSeparator ?? ', ';

	// Sort numbers to ensure they're in order
	const sortedNumbers = [...numbers].sort((a, b) => a - b);
	const ranges: string[] = [];
	let start = sortedNumbers[0];
	let prev = start;

	for (let i = 1; i <= sortedNumbers.length; i++) {
		const current = sortedNumbers[i];

		// If we're at the end or there's a gap
		if (i === sortedNumbers.length || current !== prev + 1) {
			if (start === prev) {
				ranges.push(start.toString());
			} else {
				ranges.push(`${start}${rangeSeparator}${prev}`);
			}
			start = current;
		}
		prev = current;
	}

	return ranges.join(joinSeparator);
};

export const IMAGE_CREATION_TEMPLATE_PROMPTS: TemplatePrompts[] = [
	TemplatePrompts.CREATE_LEARNING_POSTER,
	TemplatePrompts.CREATE_IMAGE,
];

// The templates offered on the Muki start page (KI Tools landing): the free-draft
// templates plus the image and podcast tools. This is the allowlist a dashboard
// launch link may target — see `templateLaunchParams`.
export const startPageTemplatePromptIds: TemplatePrompts[] = [
	...(assistantTemplatePrompts[ActiveView.FREE_DRAFT_ENTRY] ?? []).map(({ id }) => id),
	...mediaCreationTools.map(({ id }) => id),
	...podcastTools.map(({ id }) => id),
];

// Start-page templates gated behind `!user.isSchoolUser` (image + podcast). A launch
// targeting one of these for a school user must fall back to the normal landing.
export const schoolRestrictedTemplatePromptIds: TemplatePrompts[] = [
	...mediaCreationTools.map(({ id }) => id),
	...podcastTools.map(({ id }) => id),
];

export const isImageGenerationTemplate = (id: TemplatePrompts): boolean =>
	IMAGE_CREATION_TEMPLATE_PROMPTS.includes(id);

export const getTemplatePromptsByView = (view: ActiveView) => {
	return assistantTemplatePrompts[view] || [];
};

// Single source for the Muki start-page action cards, shared by the KI Tools landing
// (`AIToolsPageContent`) and the dashboard carousel (`MukiActionCarousel`) so the two
// can't drift on card set / order / entitlement. Image + podcast are gated behind
// `!user.isSchoolUser`; the free-draft templates are always included.
export const getStartPageActionCards = (
	user?: { isSchoolUser?: boolean } | null
): TemplatePromptItem<TemplatePrompts>[] => {
	const isMediaEnabled = !user?.isSchoolUser;
	return [
		...(isMediaEnabled ? podcastTools : []),
		...(isMediaEnabled ? mediaCreationTools : []),
		...getTemplatePromptsByView(ActiveView.FREE_DRAFT_ENTRY),
	];
};

// illegal characters break the downloaded file name in Safari
export const sanitizeFilename = (name: string) => {
	return (
		name
			.replace(/\s+/g, ' ') // collapse whitespace/newlines
			// biome-ignore lint/suspicious/noControlCharactersInRegex: intentional control-character matching
			.replace(/[/\\:*?"<>|\x00-\x1f]/g, '') // remove filesystem-illegal chars
			.trim()
			.replace(/\s/g, '_')
	); // spaces → underscores
};

// Fetches a remote file and saves it via a temporary anchor so we control the
// filename. Needed because a cross-origin, pre-signed URL ignores the anchor's
// `download` attribute — only a same-origin blob URL honours it. The filename
// can be derived from the fetched blob (e.g. to match its MIME type).
const downloadFromUrl = async (
	src: string,
	filename: string | ((blob: Blob) => string)
): Promise<void> => {
	try {
		const res = await fetch(src);
		if (!res.ok) {
			throw new Error(`download fetch failed: ${res.status} ${res.statusText}`);
		}
		const blob = await res.blob();
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = typeof filename === 'function' ? filename(blob) : filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(url);
	} catch (error) {
		notifyBugsnagHandledError(error as Error);
		// Cross-origin without CORS — let the user save it manually from a new tab.
		const popup = window.open(src, '_blank', 'noopener,noreferrer');
		if (!popup) {
			notifyBugsnagHandledError(
				new Error('download fallback failed: window.open returned null (popup blocked)')
			);
		}
	}
};

export const downloadImage = async (src: string, alt?: string): Promise<void> => {
	const sanitizedName = sanitizeFilename(alt || 'image');
	await downloadFromUrl(src, sanitizedName || 'image');
};

export type AudioDownloadFormat = 'wav' | 'mp3';

// Resolves with the downloaded format, or null when the blob fetch failed and
// the file was only opened in a new tab (no format is known on that path).
export const downloadAudio = async (
	src: string,
	title?: string
): Promise<AudioDownloadFormat | null> => {
	const sanitizedName = sanitizeFilename(title || 'podcast') || 'podcast';
	// Podcasts generated before the MP3 switch stay stored as WAV — the blob's
	// MIME type is the ground truth, so the saved extension never mislabels the bytes.
	let format: AudioDownloadFormat | null = null;
	await downloadFromUrl(src, (blob) => {
		format = blob.type === 'audio/wav' ? 'wav' : 'mp3';
		return `${sanitizedName}.${format}`;
	});
	return format;
};
